import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import { FileText, Bell, AlertTriangle, Clock, Building2 } from 'lucide-react';
import { PendingChangesWidget } from '../components/PendingChangesWidget';
import { Button } from '../components/ui/button';

export const Dashboard = () => {
  const { user, tenant, isCliente } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    trademarks: 0,
    clients: 0,
    alertsPending: 0,
    alertsOverdue: 0,
    alertsNext30: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchStats() {
    console.log('[Dashboard] bootstrap start');
    setLoading(true);
    try {
      const [trademarksRes, clientsRes, kpisRes] = await Promise.all([
        apiClient.get('/trademarks', { params: { limit: 1000 } }),
        apiClient.get('/clients', { params: { limit: 1000 } }),
        apiClient.get('/alerts/kpis')
      ]);

      const trademarksData = trademarksRes.data?.items || trademarksRes.data || [];
      const clientsData = clientsRes.data?.items || clientsRes.data || [];
      const totalTrademarks = trademarksRes.data?.total !== undefined ? trademarksRes.data.total : trademarksData.length;

      setStats({
        trademarks: totalTrademarks,
        clients: clientsData.filter(c => c.activo !== false).length,
        alertsPending: kpisRes.data?.pending || 0,
        alertsOverdue: kpisRes.data?.overdue || 0,
        alertsNext30: kpisRes.data?.next_30_days || 0
      });
      console.log('[Dashboard] bootstrap success');
    } catch (error) {
      console.error('[Dashboard] Error fetching stats:', error);
    } finally {
      setLoading(false);
      console.log('[Dashboard] bootstrap end');
    }
  };

  const statCards = [
    {
      name: 'Marcas Totales',
      value: stats.trademarks,
      icon: FileText,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      onClick: () => navigate('/trademarks')
    },
    {
      name: 'Alertas Pendientes',
      value: stats.alertsPending,
      icon: Bell,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-100',
      onClick: () => navigate('/alerts'),
      hide: isCliente()
    },
    {
      name: 'Alertas Vencidas',
      value: stats.alertsOverdue,
      icon: AlertTriangle,
      color: stats.alertsOverdue > 0 ? 'text-red-600' : 'text-gray-400',
      bgColor: stats.alertsOverdue > 0 ? 'bg-red-100' : 'bg-gray-100',
      onClick: () => navigate('/alerts'),
      urgent: stats.alertsOverdue > 0,
      hide: isCliente()
    },
    {
      name: 'Próximas 30 días',
      value: stats.alertsNext30,
      icon: Clock,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-100',
      onClick: () => navigate('/alerts'),
      hide: isCliente()
    },
    {
      name: 'Clientes',
      value: stats.clients,
      icon: Building2,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
      onClick: () => navigate('/clients'),
      hide: isCliente()
    },
  ];

  return (
    <>
      <div className="space-y-6" data-testid="dashboard-content">
        {/* Welcome */}
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-1">
            Bienvenido, {user?.nombre || 'Usuario'}
          </h2>
          <p className="text-sm text-gray-500">
            {tenant?.nombre || 'Cargando despacho...'}
          </p>
        </div>

        {/* Pending Changes Widget */}
        <PendingChangesWidget />

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {statCards.filter(stat => !stat.hide).map((stat) => {
            const Icon = stat.icon;
            return (
              <button
                key={stat.name}
                onClick={stat.onClick}
                className={`bg-white rounded-lg border ${stat.urgent ? 'border-red-300' : 'border-gray-200'} p-6 text-left hover:shadow-md transition-shadow`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className={`p-2 ${stat.bgColor} rounded-lg`}>
                    <Icon className={`h-5 w-5 ${stat.color}`} />
                  </div>
                  {stat.urgent && (
                    <span className="text-xs font-medium text-red-600 bg-red-100 px-2 py-0.5 rounded">
                      URGENTE
                    </span>
                  )}
                </div>
                <div>
                  <p className="text-3xl font-semibold text-gray-900">
                    {loading ? '...' : stat.value}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">{stat.name}</p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Alerts Banner if overdue */}
        {!isCliente() && stats.alertsOverdue > 0 && (
          <div
            className="bg-red-50 border border-red-200 rounded-lg p-4 cursor-pointer hover:bg-red-100 transition-colors"
            onClick={() => navigate('/alerts')}
          >
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              <div>
                <p className="font-medium text-red-900">
                  Tienes {stats.alertsOverdue} alerta{stats.alertsOverdue !== 1 ? 's' : ''} vencida{stats.alertsOverdue !== 1 ? 's' : ''}
                </p>
                <p className="text-sm text-red-700">
                  Requieren atención inmediata. Haz clic para ver detalles.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Info cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* User info */}
          <div className="bg-white rounded-lg border border-gray-200 p-4 md:p-6 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Información de Usuario</h3>
            <dl className="space-y-3">
              <div>
                <dt className="text-xs text-gray-500 uppercase tracking-wide">Nombre</dt>
                <dd className="text-sm text-gray-900 mt-1 font-medium">{user?.nombre}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500 uppercase tracking-wide">Email</dt>
                <dd className="text-sm text-gray-900 mt-1">{user?.email}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500 uppercase tracking-wide">Rol</dt>
                <dd className="text-sm text-gray-900 mt-1">
                  {user?.role === 'ADMIN_DESPACHO' && 'Administrador del Despacho'}
                  {user?.role === 'OPERADOR' && 'Operador'}
                  {user?.role === 'CLIENTE' && 'Cliente'}
                  {user?.role === 'SUPER_ADMIN' && 'Super Administrador'}
                </dd>
              </div>
            </dl>
          </div>

          {/* Tenant info */}
          <div className="bg-white rounded-lg border border-gray-200 p-4 md:p-6 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Información del Despacho</h3>
            <dl className="space-y-3">
              <div>
                <dt className="text-xs text-gray-500 uppercase tracking-wide">Nombre</dt>
                <dd className="text-sm text-gray-900 mt-1 font-medium">{tenant?.nombre}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500 uppercase tracking-wide">Dominio</dt>
                <dd className="text-sm text-gray-900 mt-1">{tenant?.dominio || 'No configurado'}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500 uppercase tracking-wide">Estado</dt>
                <dd className="mt-1">
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${tenant?.activo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {tenant?.activo ? 'Activo' : 'Inactivo'}
                  </span>
                </dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Quick actions for client */}
        {isCliente() && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="text-sm font-semibold text-blue-900 mb-2">Vista de Cliente</h3>
            <p className="text-sm text-blue-700">
              Como cliente, puede ver sus marcas asignadas y recibir notificaciones. Las funciones administrativas están restringidas.
            </p>
          </div>
        )}
      </div>
    </>
  );
};
